import { platform } from './device';

export type NotifyPermission = NotificationPermission | 'unsupported' | 'needs-install';

/**
 * Notification availability. iOS only exposes the Notification API to web
 * apps added to the Home Screen (iOS 16.4+), so a Safari tab reports
 * `needs-install` instead of pretending notifications work.
 */
export function notificationPermission(): NotifyPermission {
  if (typeof Notification === 'undefined') {
    return platform.isIOS && !platform.isStandalone ? 'needs-install' : 'unsupported';
  }
  return Notification.permission;
}

/** Must be called from a user gesture (a tap) on iOS. */
export async function requestNotificationPermission(): Promise<NotifyPermission> {
  if (typeof Notification === 'undefined') return notificationPermission();
  if (Notification.permission !== 'default') return Notification.permission;
  try {
    return await Notification.requestPermission();
  } catch {
    return Notification.permission;
  }
}

export interface NotifyOptions {
  body?: string;
  tag?: string;
  /** Path inside the app to open when the notification is tapped. */
  url?: string;
  /** Only notify when the app is not on screen (the default). */
  onlyWhenHidden?: boolean;
  icon?: string;
}

/**
 * Shows a system notification through the service worker (required on
 * Android and iOS; `new Notification()` throws there).
 */
export async function notify(title: string, opts: NotifyOptions = {}): Promise<boolean> {
  if (notificationPermission() !== 'granted') return false;
  if ((opts.onlyWhenHidden ?? true) && document.visibilityState === 'visible') return false;
  try {
    const reg = await navigator.serviceWorker?.getRegistration();
    if (!reg) return false;
    await reg.showNotification(title, {
      body: opts.body,
      tag: opts.tag,
      icon: opts.icon ?? '/icons/icon-192.png',
      badge: '/icons/badge-96.png',
      data: { url: opts.url ?? '/' },
    });
    return true;
  } catch (err) {
    console.warn('[notify] failed', err);
    return false;
  }
}
